import IMask from "imask"

// Get locale-specific number format from server-provided I18n config
function getNumberFormat() {
  return window.I18n?.numberFormat || { decimal: ".", group: "," }
}

const getDefaultConfig = () => {
  const format = getNumberFormat()
  return {
    mask: Number,
    thousandsSeparator: format.group,
    padFractionalZeros: false, // if true, then pads zeros at end to the length of scale.
    normalizeZeros: false, // appends or removes zeros at ends
    radix: format.decimal, // fractional delimiter
  }
}

let uid = 0

export const NumberInput = {
  mounted() {
    this.attach()
  },

  updated() {
    this.attach()
  },

  attach() {
    const hiddenElement = this.el
    this.changedSinceLastProxyChange = false
    this.inputElement = this.el.parentNode.querySelector(
      'input[data-role="main"]',
    )

    // Phoenix will not call the [updated] hook if the element has not changed.
    // To force the update to happen, we set an attribute with an arbitrary value.
    uid++
    hiddenElement.setAttribute("bogus-attr", uid)

    // Paradoxal but important as updates cannot be done otherwise
    hiddenElement.style.display = "inline"

    // Prevent Phoenix from update
    this.inputElement.addEventListener("input", (event) => {
      event.stopPropagation()
    })

    // The visible masked input has no name, so letting its native blur/change
    // bubble to LiveView produces _target=["undefined"]. Forward blur validation
    // through the hidden named input only when IMask accepted a real value
    // change; otherwise a redundant validation can clear transient server state
    // such as calculated-amount auto-switch markers before the user's next click.
    this.inputElement.addEventListener("change", (event) => {
      event.stopPropagation()

      if (this.changedSinceLastProxyChange) {
        this.changedSinceLastProxyChange = false
        hiddenElement.dispatchEvent(new Event("change", { bubbles: true }))
        // Parent forms commonly use phx-debounce="blur", which holds the
        // change event until the source input blurs. The hidden input here
        // is the form-participating one (named, value-carrying) but it
        // isn't focusable and never receives a real blur — so without this
        // synthetic blur the change is held indefinitely and validate
        // never fires.
        hiddenElement.dispatchEvent(new FocusEvent("blur", { bubbles: true }))
      }
    })

    if (this.mask) {
      this.mask.destroy()
    }

    const mask = IMask(this.inputElement, this.getConfig())

    if (hiddenElement.value === "") {
      mask.unmaskedValue = ""
    } else {
      mask.typedValue = Number(hiddenElement.value)
    }

    mask.on("accept", () => {
      hiddenElement.value = mask.unmaskedValue
      this.changedSinceLastProxyChange = true
      hiddenElement.dispatchEvent(new Event("input", { bubbles: true }))
    })

    this.mask = mask
  },

  getConfig() {
    function datasetBoolValue(el, prop) {
      return prop in el.dataset && el.dataset[prop] !== "false"
    }

    function datasetIntValue(el, prop, defaultValue = 0) {
      if (!(prop in el.dataset)) return defaultValue
      const rawValue = el.dataset[prop]
      if (rawValue === "") return defaultValue
      return parseInt(rawValue, 10)
    }

    const allowDecimal = datasetBoolValue(this.inputElement, "allowDecimal")
    const maxDecimals = datasetIntValue(this.inputElement, "maxDecimals", 4)
    const min = this.inputElement.dataset.min

    return {
      ...getDefaultConfig(),
      scale: allowDecimal ? maxDecimals : 0,
      ...(min === undefined ? {} : { min: Number(min) }),
    }
  },
}
